import React from 'react';
import { Result, Button, Space, Typography, Collapse } from 'antd';
import { ReloadOutlined, HomeOutlined, BugOutlined, InfoCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { openSupportWithError } from '../../utils/openSupportWithError';

const { Text, Paragraph } = Typography;

interface GlobalErrorFallbackProps {
  error: Error;
  onReset: () => void;
}

export const GlobalErrorFallback: React.FC<GlobalErrorFallbackProps> = ({ 
  error, 
  onReset 
}) => {
  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleReport = () => {
    openSupportWithError({
      errorMessage: error.message,
      stack: error.stack,
    });
  };

  const isDevelopment = import.meta.env.DEV;

  return (
    <div 
      style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        padding: '24px',
        background: '#f5f5f5'
      }}
    >
      <Result
        status="error"
        title="Произошла ошибка"
        subTitle="Не удалось загрузить данные. Попробуйте повторить операцию или вернитесь на главную страницу."
        extra={
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space>
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={onReset}
              >
                Попробовать снова
              </Button>
              <Button 
                icon={<HomeOutlined />}
                onClick={handleGoHome}
              >
                На главную
              </Button>
              <Button
                icon={<BugOutlined />}
                onClick={handleReport}
              >
                Сообщить о проблеме
              </Button>
            </Space>

            <Collapse
              ghost
              style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'left' }}
              items={[
                {
                  key: 'details',
                  label: (
                    <Space>
                      <InfoCircleOutlined />
                      <Text strong>Детали ошибки</Text>
                    </Space>
                  ),
                  children: (
                    <div style={{ padding: '8px 0' }}>
                      <Paragraph
                        code
                        style={{
                          backgroundColor: '#f6f8fa',
                          padding: 12,
                          borderRadius: 4,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontSize: '12px',
                          maxHeight: '300px',
                          overflow: 'auto',
                          marginBottom: 8,
                        }}
                      >
                        {[
                          `Сообщение: ${error.message}`,
                          `Время: ${new Date().toLocaleString('ru-RU')}`,
                          `Страница: ${window.location.pathname}`,
                          ...(isDevelopment && error.stack ? [`\nStack trace:\n${error.stack}`] : []),
                        ].join('\n')}
                      </Paragraph>
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => {
                          const text = [
                            `Сообщение: ${error.message}`,
                            `Время: ${new Date().toLocaleString('ru-RU')}`,
                            `Страница: ${window.location.pathname}`,
                            `URL: ${window.location.href}`,
                            ...(error.stack ? [`\nStack trace:\n${error.stack}`] : []),
                          ].join('\n');
                          navigator.clipboard.writeText(text).catch(() => {
                            const textarea = document.createElement('textarea');
                            textarea.value = text;
                            textarea.style.position = 'fixed';
                            textarea.style.opacity = '0';
                            document.body.appendChild(textarea);
                            textarea.select();
                            document.execCommand('copy');
                            document.body.removeChild(textarea);
                          });
                        }}
                      >
                        Скопировать детали
                      </Button>
                    </div>
                  ),
                },
              ]}
            />
          </Space>
        }
      />
    </div>
  );
};

export default GlobalErrorFallback;
